import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MessageCircle } from 'lucide-react';
import { ROUTES } from '../routes';
import { useServices } from '../hooks/useServices';
import ChatListSkeleton from '../components/ChatListSkeleton';

interface ChatConversation {
  id: string;
  companionId: string;
  companionName: string;
  companionImage: string;
  lastMessage: string;
  lastMessageTime: string;
  unreadCount?: number;
}

const ChatListPage: React.FC = () => {
  const navigate = useNavigate();
  const { chatService } = useServices();
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    try {
      setLoading(true);

      // Buscar conversas reais da API
      const response = await chatService.getChats();

      console.log('API Response:', response);

      if (response.success) {
        // The backend returns ChatListResponse with a 'chats' property
        const chatListResponse = response.data;
        const chats = chatListResponse.chats;

        // Converter os dados da API para o formato esperado pela interface
        const conversations: ChatConversation[] = chats.map((chat) => {
          // Pegar a última mensagem se existir
          const lastMessage =
            chat.messages && chat.messages.length > 0
              ? chat.messages[chat.messages.length - 1]
              : null;

          // Formatar o timestamp da última mensagem
          const lastMessageTime = lastMessage
            ? new Date(lastMessage.timestamp).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              })
            : '';

          return {
            id: `${chat.user_id}:${chat.companion_id}`,
            companionId: chat.companion_id,
            companionName: chat.companion.name,
            companionImage: chat.companion.image,
            lastMessage: lastMessage ? lastMessage.content : 'No messages yet',
            lastMessageTime,
            unreadCount: 0, // TODO: Implementar contagem de mensagens não lidas
          };
        });

        console.log('Converted conversations:', conversations);
        setConversations(conversations);
      } else {
        console.error('Failed to load conversations:', response.message);
        setConversations([]);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChatClick = (companionId: string) => {
    navigate(ROUTES.CHAT_WITH_COMPANION(companionId));
  };

  const filteredConversations = conversations.filter((conv) =>
    conv.companionName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <ChatListSkeleton />;
  }

  return (
    <div className="h-full flex flex-col bg-kind-background">
      {/* Header */}
      <div className="p-6 border-b border-gray-300 dark:border-white/10">
        <h1 className="text-3xl font-title font-medium text-kind-text-primary mb-6">Chat</h1>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-kind-text-secondary w-5 h-5" />
          <input
            type="text"
            placeholder="Search for a profile..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-kind-container border border-gray-300 dark:border-white/10 rounded-xl text-kind-text-primary placeholder-kind-text-secondary focus:outline-none focus:border-kind-purple transition-colors"
          />
        </div>
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto">
        {filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-6">
            <MessageCircle className="w-16 h-16 text-kind-text-secondary mb-4" />
            <h3 className="text-xl font-title font-medium text-kind-text-primary mb-2">
              {searchQuery ? 'No conversations found' : 'No conversations yet'}
            </h3>
            <p className="text-kind-text-secondary mb-6">
              {searchQuery
                ? 'Try adjusting your search terms'
                : 'Start a conversation with a companion to see it here'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => navigate(ROUTES.HOME)}
                className="px-6 py-3 bg-kind-purple text-white rounded-xl font-medium hover:bg-kind-purple/90 transition-colors"
              >
                Browse Companions
              </button>
            )}
          </div>
        ) : (
          <div className="p-4 space-y-2">
            {filteredConversations.map((conversation) => (
              <div
                key={conversation.id}
                onClick={() => handleChatClick(conversation.companionId)}
                className="flex items-center p-4 rounded-xl hover:bg-kind-container/50 cursor-pointer transition-colors group"
              >
                {/* Avatar */}
                <div className="relative flex-shrink-0 mr-4">
                  <img
                    src={conversation.companionImage}
                    alt={conversation.companionName}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-title font-medium text-kind-text-primary truncate">
                      {conversation.companionName}
                    </h3>
                    <span className="text-xs text-kind-text-secondary ml-2">
                      {conversation.lastMessageTime}
                    </span>
                  </div>
                  <p className="text-sm text-kind-text-secondary truncate">
                    {conversation.lastMessage}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatListPage;
